import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Finding, Severity, TrainingExample, GEA_DEFECT_REGISTRY } from '../types';
import { updateVaultEntry, deleteVaultEntry } from '../services/vaultService';

interface VaultEntryEditorProps {
  entry: TrainingExample;
  onClose: () => void;
  onSaved: () => void;   // called after successful save so parent can refresh list
  onDeleted: () => void; // called after successful delete
}

type DragMode = null | 'move' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se';

const severityColors: Record<Severity, string> = {
  [Severity.NONE]: '#64748b',
  [Severity.LOW]: '#3b82f6',
  [Severity.MEDIUM]: '#eab308',
  [Severity.HIGH]: '#f97316',
  [Severity.CRITICAL]: '#ef4444',
};

// Box format: [ymin, xmin, ymax, xmax] normalized 0..1000 (matches pdfService / overlay)
const clamp = (v: number, lo = 0, hi = 1000) => Math.max(lo, Math.min(hi, v));

export const VaultEntryEditor: React.FC<VaultEntryEditorProps> = ({ entry, onClose, onSaved, onDeleted }) => {
  // The vault entries we care about have exactly ONE finding after the v2 split.
  // If for some reason there are 0 or >1, we still just edit the first one and
  // preserve the rest (defensive — shouldn't happen in practice).
  const initialFinding = entry.analysis?.findings?.[0];

  const [finding, setFinding] = useState<Finding | null>(initialFinding ? { ...initialFinding } : null);
  const [description, setDescription] = useState<string>(entry.analysis?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const [imgNaturalSize, setImgNaturalSize] = useState<{ w: number; h: number } | null>(null);

  const imageSrc = entry.image || entry.imageUrl || '';

  // Drag state
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const dragStartRef = useRef<{ x: number; y: number; box: [number, number, number, number] } | null>(null);

  // Observe container size (fires on tab/modal mount and resize)
  useEffect(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setContainerSize({ w: rect.width, h: rect.height });
    }
    const observer = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = e.contentRect.width;
        const h = e.contentRect.height;
        if (w > 0 && h > 0) setContainerSize({ w, h });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Load image natural dimensions
  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => setImgNaturalSize({ w: img.width, h: img.height });
    img.src = imageSrc;
  }, [imageSrc]);

  const layout = useMemo(() => {
    if (!imgNaturalSize || !containerSize) return null;
    const { w: cw, h: ch } = containerSize;
    const { w: iw, h: ih } = imgNaturalSize;
    const ratio = Math.min(cw / iw, ch / ih);
    const displayW = iw * ratio;
    const displayH = ih * ratio;
    const offsetX = (cw - displayW) / 2;
    const offsetY = (ch - displayH) / 2;
    return { displayW, displayH, offsetX, offsetY };
  }, [imgNaturalSize, containerSize]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Registry entry for current finding type (for color)
  const registryEntry = finding ? (GEA_DEFECT_REGISTRY.find(d => d.type === finding.type || d.title === finding.type)) : null;
  const catColor = registryEntry?.hex || '#0000a9';

  // Convert a pixel delta (within the image display area) to a 0..1000 normalized delta
  const pxToNormDx = (dx: number) => (layout ? (dx / layout.displayW) * 1000 : 0);
  const pxToNormDy = (dy: number) => (layout ? (dy / layout.displayH) * 1000 : 0);

  const onMouseDownBox = (e: React.MouseEvent, mode: DragMode) => {
    if (!finding?.box) return;
    e.stopPropagation();
    setDragMode(mode);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      box: [...finding.box] as [number, number, number, number],
    };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragMode || !dragStartRef.current || !finding?.box) return;
    const dx = pxToNormDx(e.clientX - dragStartRef.current.x);
    const dy = pxToNormDy(e.clientY - dragStartRef.current.y);
    const [ymin0, xmin0, ymax0, xmax0] = dragStartRef.current.box;

    let next: [number, number, number, number] = [ymin0, xmin0, ymax0, xmax0];
    if (dragMode === 'move') {
      const w = xmax0 - xmin0;
      const h = ymax0 - ymin0;
      const newXmin = clamp(xmin0 + dx, 0, 1000 - w);
      const newYmin = clamp(ymin0 + dy, 0, 1000 - h);
      next = [newYmin, newXmin, newYmin + h, newXmin + w];
    } else if (dragMode === 'resize-nw') {
      next = [clamp(ymin0 + dy, 0, ymax0 - 10), clamp(xmin0 + dx, 0, xmax0 - 10), ymax0, xmax0];
    } else if (dragMode === 'resize-ne') {
      next = [clamp(ymin0 + dy, 0, ymax0 - 10), xmin0, ymax0, clamp(xmax0 + dx, xmin0 + 10, 1000)];
    } else if (dragMode === 'resize-sw') {
      next = [ymin0, clamp(xmin0 + dx, 0, xmax0 - 10), clamp(ymax0 + dy, ymin0 + 10, 1000), xmax0];
    } else if (dragMode === 'resize-se') {
      next = [ymin0, xmin0, clamp(ymax0 + dy, ymin0 + 10, 1000), clamp(xmax0 + dx, xmin0 + 10, 1000)];
    }

    setFinding({ ...finding, box: next });
  };

  const onMouseUp = () => {
    setDragMode(null);
    dragStartRef.current = null;
  };

  const handleSave = async () => {
    if (!finding) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      // Ensure the new entry carries base64 image data — NAS needs it to write the new .jpg.
      // If entry already has base64 (rare), reuse. Otherwise fetch from imageUrl and convert.
      let base64Image = entry.image;
      if (!base64Image && entry.imageUrl) {
        const resp = await fetch(entry.imageUrl);
        if (!resp.ok) throw new Error(`Failed to fetch source image (${resp.status})`);
        const blob = await resp.blob();
        base64Image = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
      }
      if (!base64Image) throw new Error('No image data available to save.');

      // Build the updated entry. timestamp advances to now (signals "this was edited"),
      // sourceGroup preserved so sibling grouping is intact, editedFromTimestamp tracks origin.
      const now = Date.now();
      const originalTs = entry.timestamp;

      const nextEntry: TrainingExample = {
        ...entry,
        image: base64Image,
        timestamp: now,
        sourceGroup: entry.sourceGroup ?? entry.timestamp,
        splitVersion: 1,
        // @ts-ignore — editedFromTimestamp is a runtime-only field not in TrainingExample type yet
        editedFromTimestamp: originalTs,
        analysis: {
          ...entry.analysis,
          findings: [finding],
          description,
        },
      };

      await updateVaultEntry(originalTs, nextEntry);
      onSaved();
      onClose();
    } catch (e: any) {
      console.error('[VaultEntryEditor] save failed:', e);
      setSaveError(e?.message || 'Save failed — see console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Permanently evict this verified finding from the vault?')) return;
    setIsDeleting(true);
    try {
      await deleteVaultEntry(entry.timestamp);
      onDeleted();
      onClose();
    } catch (e) {
      console.error('[VaultEntryEditor] delete failed:', e);
      setIsDeleting(false);
    }
  };

  // Render
  if (!finding) {
    return (
      <div className="fixed inset-0 z-[100] bg-slate-900/80 flex items-center justify-center p-8">
        <div className="bg-slate-300 p-8 max-w-md">
          <h2 className="text-[14px] font-black text-[#0000a9] uppercase tracking-widest mb-4">No Finding</h2>
          <p className="text-[12px] text-slate-800 mb-6">This entry has no finding to edit.</p>
          <button onClick={onClose} className="bg-[#0000a9] text-white px-6 py-2 text-[10px] font-black uppercase tracking-widest">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-stretch p-6" onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
      <div className="flex-1 bg-slate-200 flex flex-col shadow-2xl border-2 border-[#0000a9]">

        {/* Header */}
        <div className="bg-slate-900 text-white px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[18px] font-black tracking-widest uppercase italic">Vault Entry Editor</div>
            <div className="text-[9px] font-mono text-slate-400 uppercase tracking-widest mt-1">
              Editing entry · ID {entry.timestamp} · Verified {new Date(entry.timestamp).toLocaleDateString()}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-[24px] px-4 transition" aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">

          {/* Canvas */}
          <div className="flex-1 bg-slate-500/30 p-8 flex items-center justify-center">
            <div
              ref={containerRef}
              className="w-full h-full bg-slate-900 relative overflow-hidden border-2 border-slate-600"
            >
              {layout && imageSrc ? (
                <>
                  <img
                    src={imageSrc}
                    alt="Vault entry"
                    className="absolute pointer-events-none select-none"
                    style={{
                      left: layout.offsetX,
                      top: layout.offsetY,
                      width: layout.displayW,
                      height: layout.displayH,
                      objectFit: 'fill',
                    }}
                    draggable={false}
                  />
                  {/* Box overlay */}
                  {finding.box && finding.box.length >= 4 && (() => {
                    const [ymin, xmin, ymax, xmax] = finding.box;
                    const boxLeft = layout.offsetX + (xmin / 1000) * layout.displayW;
                    const boxTop = layout.offsetY + (ymin / 1000) * layout.displayH;
                    const boxW = ((xmax - xmin) / 1000) * layout.displayW;
                    const boxH = ((ymax - ymin) / 1000) * layout.displayH;
                    const handle = 'absolute w-3 h-3 bg-white border-2 shadow-lg';
                    return (
                      <div
                        className="absolute border-2 cursor-move"
                        style={{ left: boxLeft, top: boxTop, width: boxW, height: boxH, borderColor: catColor, backgroundColor: `${catColor}20` }}
                        onMouseDown={(e) => onMouseDownBox(e, 'move')}
                      >
                        <div className="absolute -top-6 left-0 px-2 py-0.5 text-[9px] font-black uppercase whitespace-nowrap" style={{ backgroundColor: catColor, color: '#fff' }}>
                          {registryEntry?.title || finding.type}
                        </div>
                        <div className={handle} style={{ top: -6, left: -6, borderColor: catColor, cursor: 'nwse-resize' }} onMouseDown={(e) => onMouseDownBox(e, 'resize-nw')} />
                        <div className={handle} style={{ top: -6, right: -6, borderColor: catColor, cursor: 'nesw-resize' }} onMouseDown={(e) => onMouseDownBox(e, 'resize-ne')} />
                        <div className={handle} style={{ bottom: -6, left: -6, borderColor: catColor, cursor: 'nesw-resize' }} onMouseDown={(e) => onMouseDownBox(e, 'resize-sw')} />
                        <div className={handle} style={{ bottom: -6, right: -6, borderColor: catColor, cursor: 'nwse-resize' }} onMouseDown={(e) => onMouseDownBox(e, 'resize-se')} />
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-[11px] font-mono uppercase tracking-widest">
                  Loading image…
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-[380px] bg-slate-300 p-8 overflow-y-auto flex flex-col">
            <div className="mb-6">
              <div className="text-[12px] font-black text-slate-900 uppercase tracking-widest mb-1">Classification</div>
              <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-3">Defining ground truth</div>
              <select
                value={finding.type}
                onChange={(e) => setFinding({ ...finding, type: e.target.value })}
                className="w-full bg-white border-2 border-slate-400 px-3 py-3 text-[12px] font-black text-slate-900 uppercase"
              >
                {GEA_DEFECT_REGISTRY.map(d => (
                  <option key={d.type} value={d.type}>{d.title}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <div className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-3">Adjust Criticality <span className="text-slate-600 font-mono">(priority level)</span></div>
              <div className="grid grid-cols-4 gap-1">
                {[Severity.LOW, Severity.MEDIUM, Severity.HIGH, Severity.CRITICAL].map(sev => {
                  const isActive = finding.severity === sev;
                  const color = severityColors[sev];
                  return (
                    <button
                      key={sev}
                      onClick={() => setFinding({ ...finding, severity: sev })}
                      className="py-3 text-[9px] font-black uppercase tracking-wider transition-all"
                      style={{
                        backgroundColor: isActive ? color : '#f1f5f9',
                        color: isActive ? '#fff' : '#0f172a',
                      }}
                    >
                      {sev}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-6">
              <div className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-3">Defect Detail Description</div>
              <textarea
                value={finding.description || ''}
                onChange={(e) => setFinding({ ...finding, description: e.target.value })}
                rows={4}
                className="w-full bg-white border-2 border-slate-400 px-3 py-3 text-[11px] text-slate-900 resize-none"
                placeholder="Describe the defect..."
              />
            </div>

            <div className="mb-6">
              <div className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-3">Entry Description</div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full bg-white border-2 border-slate-400 px-3 py-3 text-[11px] text-slate-900 resize-none"
                placeholder="Entry-level note..."
              />
            </div>

            <button
              onClick={handleDelete}
              disabled={isDeleting || isSaving}
              className="w-full bg-slate-400 text-red-600 py-3 text-[10px] font-black uppercase tracking-widest mb-3 disabled:opacity-50 hover:bg-red-100 transition"
            >
              {isDeleting ? 'Evicting…' : 'Delete Anomaly'}
            </button>

            {/* Spacer pushes Save/Cancel to bottom */}
            <div className="flex-1" />

            {saveError && (
              <div className="mb-3 p-3 bg-red-100 border-l-4 border-red-500 text-[10px] text-red-800 font-mono">
                {saveError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={onClose}
                disabled={isSaving || isDeleting}
                className="py-3 bg-slate-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-slate-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || isDeleting}
                className="py-3 bg-[#0000a9] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#0000c0] transition disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
